// Вставка и импорт (агент paste_import): «скопировать что угодно из Word и вставить» - главная задача модуля.
//   Ctrl+V из Word / LibreOffice / Google Документов / браузера (Википедия и т. п.) / Excel-фрагментов с текстом:
//     структура СТО (заголовки без ручных номеров, перечисления трёх видов, рисунки с подписями, таблицы с головкой
//     и объединениями, формулы Word -> LaTeX, «где», сноски, примечания, источники и ссылки [N], перекрёстные ссылки),
//     оформление (шрифты, цвета, отступы, mso-стили) отбрасывается; после вставки - плашка с итогом и вариантами.
//   Ctrl+Shift+V - только текст (многострочный текст из PDF склеивается в абзацы).
//   Markdown (чат-боты, README, диалект writer) - заголовки, списки, таблицы, код, $формулы$.
//   Файлы .docx/.md/.html: перетаскивание в редактор (вставить сюда или новый отчёт), на список отчётов (новый отчёт),
//   «Файл -> Открыть документ Word», «Главная -> Вставить -> Из файла…», «Вставить Markdown / текст…».
import type { RegistryApi, PasteData, Ctx } from '../../core/registry';
import { ctx as gctx } from '../../core/ctx';
import { detectApp } from './prep';
import { looksMarkdown } from './text';
import { parseInput, runPaste, finalize, type PasteInput, type PasteOpts } from './apply';
import { mountBar, showBar, setBusy, hideBar, repaste, currentSession } from './bar';
import { getPref, setPref } from './prefs';
import { DocChoiceDialog, MarkdownDialog, DOC_EXT, WORD_EXT, importAsProject, insertDocFile, pickFile } from './ui';
import './paste.css';

const INTERNAL = /data-pm-slice/;

function inputOf(d: PasteData): PasteInput {
  let codeLang = '';
  try {
    const dt = (d.event as ClipboardEvent).clipboardData || (d.event as DragEvent).dataTransfer;
    const v = dt?.getData('vscode-editor-data');
    if (v) codeLang = JSON.parse(v).mode || '';
  } catch { /* нет метаданных редактора кода */ }
  return { kind: d.kind, html: d.html, text: d.text, files: d.files, pos: d.kind === 'drop' ? d.pos : null, codeLang };
}

async function go(c: Ctx, input: PasteInput, opts: Partial<PasteOpts> = {}) {
  hideBar();
  setBusy(true);
  try {
    const s = await runPaste(c, input, { mode: 'structure', links: getPref('links'), ...opts });
    showBar(s);
  } catch (e: any) {
    c.toast('Вставка: ' + (e?.message || e), 'error', 8000);
  } finally {
    setBusy(false);
  }
}

const shiftPaste = (c: Ctx) => !!(c.editor?.view as any)?.input?.shiftKey;
const inTable = (c: Ctx) => {
  const $f = c.editor?.state.selection.$from;
  if (!$f) return false;
  for (let k = $f.depth; k > 0; k--) if (['tableCell', 'tableHeader'].includes($f.node(k).type.name)) return true;
  return false;
};
const htmlHasText = (html: string) => {
  if (!html) return false;
  const doc = new DOMParser().parseFromString(html.replace(/<!--[\s\S]*?-->/g, ''), 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, '').length > 0;
};

export default function setup(r: RegistryApi) {
  // ---------------------------------------------------------------------------------------- вставка
  // Ctrl+Shift+V: только текст (многострочный - абзацами; одна строка - стандартная вставка)
  r.paste.add({ id: 'paste.plain', priority: 200,
    match: (d, c) => d.kind === 'paste' && shiftPaste(c) && /\S\s*\n\s*\S/.test(d.text || htmlText(d.html)) && !inTable(c),
    handle: (d, c) => { void go(c, inputOf(d), { mode: 'text' }); return true; } });

  // файлы документов: .docx - спросить (вставить сюда / новый отчёт), .md/.txt/.html - вставить
  r.paste.add({ id: 'paste.docFiles', priority: 110,
    match: (d) => d.files.length > 0 && d.files.every((f) => DOC_EXT.test(f.name)),
    handle: async (d, c) => {
      for (const f of d.files) {
        if (WORD_EXT.test(f.name)) {
          const how = await c.openDialog<'insert' | 'new'>('paste.docChoice', { name: f.name });
          if (how === 'new') { await importAsProject(c, f); return; }
          if (how !== 'insert') return;
        }
        try { await insertDocFile(c, f, d.kind === 'drop' ? d.pos : null); } catch (e: any) { c.toast(`«${f.name}»: ${e.message}`, 'error', 8000); }
      }
    } });

  // VS Code: Markdown-файл - разбираем как Markdown (а не листингом)
  r.paste.add({ id: 'paste.vscodeMarkdown', priority: 91,
    match: (d) => d.kind === 'paste' && d.types.includes('vscode-editor-data') && inputOf(d).codeLang === 'markdown' && looksMarkdown(d.text),
    handle: (d, c) => { void go(c, { ...inputOf(d), html: '', markdown: true }); return true; } });

  // HTML с формулами Word/MathML (в т. ч. формула в таблице-вёрстке «формула | (N)») - раньше вставки таблиц
  r.paste.add({ id: 'paste.richMath', priority: 87,
    match: (d) => !!d.html && !INTERNAL.test(d.html) && (/msEquation/i.test(d.html) ||
      // формула со страницы без текста вокруг - обработчик формул (math.mathml); с текстом - здесь
      (/<math[\s>]|class="?katex|mwe-math-element/i.test(d.html) && htmlHasText(d.html.replace(/<math[\s\S]*?<\/math>/gi, '')
        .replace(/<annotation[\s\S]*?<\/annotation>/gi, '').replace(/<span class="?katex-html[\s\S]*?<\/span><\/span>/gi, '')))),
    handle: (d, c) => { void go(c, inputOf(d)); return true; } });

  // HTML из Word, LibreOffice, Google Документов, браузера: структура СТО
  r.paste.add({ id: 'paste.rich', priority: 80,
    match: (d, c) => {
      if (!d.html || INTERNAL.test(d.html)) return false;
      const app = detectApp(d.html);
      if (app === 'editor') return false;
      const imgs = (d.html.match(/<img\b|<v:imagedata/gi) || []).length;
      const text = htmlHasText(d.html.replace(/<math[\s\S]*?<\/math>/gi, ''));
      if (!text && /<math[\s>]/i.test(d.html) && !imgs) return false;                // одна формула MathML - math.mathml
      if (!text && imgs <= 1 && !/file:|msohtmlclip/i.test(d.html)) return false;   // одна картинка из сети - tfc.htmlRemoteImage
      // одна строка простого текста в таблицу/подпись - стандартная вставка
      if (inTable(c) && /<table/i.test(d.html) && !/<p|<h\d|<li/i.test(d.html.replace(/<table[\s\S]*<\/table>/gi, ''))) return false;
      return true;
    },
    handle: (d, c) => { void go(c, inputOf(d)); return true; } });

  // Markdown-текст (чат-боты, README, writer) - раньше таблиц из текста (tfc.tableText, 45)
  r.paste.add({ id: 'paste.markdown', priority: 50,
    match: (d, c) => d.kind === 'paste' && !d.files.length && (!d.html || /^\s*<pre|<meta[^>]*>\s*<pre/i.test(d.html)) &&
      !inTable(c) && looksMarkdown(d.text) && !pureTable(d.text),
    handle: (d, c) => { void go(c, { ...inputOf(d), html: '', markdown: true }); return true; } });

  // многострочный простой текст: абзацы (PDF - склейка строк), «-»/«а)»/«1)» -> перечисления, подписи, номера
  r.paste.add({ id: 'paste.text', priority: 40,
    match: (d, c) => d.kind === 'paste' && !d.files.length && !d.html && /\S[ \t]*\r?\n\s*\S/.test(d.text) && !inTable(c) &&
      !whereInline(c),
    handle: (d, c) => { void go(c, inputOf(d)); return true; } });

  // запасная очистка HTML Word, если вставку обработал стандартный разбор (другие обработчики)
  r.htmlTransforms.add({ id: 'word.clean', order: 10, transform: (html) => cleanWordHtml(html) });

  // ---------------------------------------------------------------------------------------- команды
  r.commands.add({ id: 'paste.markdownDialog', title: 'Вставить Markdown / текст…', icon: 'text',
    description: 'Ответ чат-бота, Markdown, текст из PDF - заголовками, списками, таблицами и формулами',
    run: (c) => c.openDialog('paste.markdown') });
  r.commands.add({ id: 'paste.fromFile', title: 'Вставить из файла…', icon: 'file-up',
    description: 'Содержимое документа Word (.docx), Markdown или HTML - на место курсора',
    run: async (c) => {
      const fs = await pickFile('.docx,.doc,.rtf,.odt,.md,.markdown,.txt,.htm,.html');
      for (const f of fs) { try { await insertDocFile(c, f, null); } catch (e: any) { c.toast(`«${f.name}»: ${e.message}`, 'error', 8000); } }
    } });
  r.commands.add({ id: 'file.importDocx', title: 'Открыть документ Word…', icon: 'import',
    description: 'Создать отчёт по СТО из готового .docx: титул и задание - в данные работы, текст - в редактор',
    run: async (c) => { const fs = await pickFile('.docx,.doc,.rtf,.odt,.md,.markdown,.txt,.zip'); for (const f of fs) await importAsProject(c, f); } });
  // программная вставка для других функций (кнопка «Вставить»/«Только текст» оболочки, перетаскивание и т. п.):
  // ctx.run('paste.run', {html?, text?, files?, mode?: 'structure'|'basic'|'text', markdown?: boolean})
  r.commands.add({ id: 'paste.run', title: 'Вставить (конвейер СТО)', icon: 'paste',
    run: (c, arg) => {
      const a = (arg || {}) as Partial<PasteInput> & { mode?: PasteOpts['mode'] };
      return go(c, { kind: 'command', html: a.html || '', text: a.text || '', files: a.files || [], pos: a.pos ?? null,
        markdown: a.markdown }, a.mode ? { mode: a.mode } : {});
    } });
  r.commands.add({ id: 'paste.lastAsText', title: 'Последнюю вставку - только текстом', icon: 'eraser',
    isEnabled: () => !!currentSession(), run: () => repaste({ mode: 'text' }) });
  const linkCmd = (k: 'text' | 'keep' | 'sources', title: string) => r.commands.add({ id: 'paste.links.' + k, title, icon: 'link',
    isActive: () => getPref('links') === k, run: () => setPref('links', k) });
  linkCmd('text', 'Гиперссылки - только текстом');
  linkCmd('keep', 'Гиперссылки - сохранять');
  linkCmd('sources', 'Гиперссылки - в список источников');

  // лента: «Главная -> Буфер обмена» (рядом со «Вставить» оболочки) и «Файл»
  r.ribbonItems.add({ id: 'paste-more', group: 'home.clip', order: 5, type: 'menu', icon: 'paste', label: 'Ещё', hint: 'Специальная вставка',
    items: () => [
      { command: 'paste.markdownDialog' },
      { command: 'paste.fromFile' },
      { separator: true },
      { label: 'Гиперссылки при вставке', children: [
        { command: 'paste.links.text', checked: getPref('links') === 'text' },
        { command: 'paste.links.keep', checked: getPref('links') === 'keep' },
        { command: 'paste.links.sources', checked: getPref('links') === 'sources' },
      ] },
    ] });
  r.ribbonGroups.add({ id: 'file.import', tab: 'file', title: 'Импорт', order: 15 });
  r.ribbonItems.add({ id: 'import-docx', group: 'file.import', type: 'large', command: 'file.importDocx', label: 'Документ Word' });
  r.ribbonItems.add({ id: 'insert-file', group: 'file.import', type: 'large', command: 'paste.fromFile', label: 'Вставить из файла' });
  r.ribbonItems.add({ id: 'insert-md', group: 'file.import', type: 'large', command: 'paste.markdownDialog', label: 'Markdown / текст' });

  r.contextMenu.add({ id: 'paste.more', order: 1, items: () => [
    { command: 'paste.markdownDialog' },
    ...(currentSession() ? [{ command: 'paste.lastAsText' }] : []),
  ] });

  r.dialogs.add({ id: 'paste.docChoice', component: DocChoiceDialog });
  r.dialogs.add({ id: 'paste.markdown', component: MarkdownDialog });

  r.events.on('editor:created', () => mountBar());
  r.events.on('project:closed', () => hideBar());

  // автотесты и отладка: разбор без вставки / вставка с параметрами
  (window as any).stoPaste = {
    parse: async (input: Partial<PasteInput>, opts: Partial<PasteOpts> = {}) => {
      const p = await parseInput(gctx, { kind: 'command', html: '', text: '', files: [], pos: null, ...input },
        { mode: 'structure', links: 'text', ...opts });
      return { ...p, blocks: finalize(p.blocks), appendices: finalize(p.appendices) };
    },
    run: async (input: Partial<PasteInput>, opts: Partial<PasteOpts> = {}) => {
      await go(gctx, { kind: 'command', html: '', text: '', files: [], pos: null, ...input }, opts);
      const s = currentSession();
      return s ? { app: s.app, stats: s.stats, images: s.images, mathErrors: s.mathErrors, blocks: s.blocksCount } : null;
    },
    repaste,
    session: () => currentSession(),
  };
}

// ------------------------------------------------------------------------------------------------ помощники
function htmlText(html: string): string {
  if (!html) return '';
  return (new DOMParser().parseFromString(html, 'text/html').body.textContent || '');
}

function pureTable(text: string): boolean {
  const lines = text.trim().split(/\r?\n/).filter((l) => l.trim());
  return lines.length > 0 && lines.every((l) => /^\s*\|.*\|\s*$/.test(l) || l.includes('\t'));
}

/** Курсор в подписи/заголовке: многострочный текст склеится в строку стандартной вставкой. */
function whereInline(c: Ctx): boolean {
  const $f = c.editor?.state.selection.$from;
  if (!$f) return false;
  for (let k = $f.depth; k > 0; k--) if (['caption', 'heading', 'appendixTitle'].includes($f.node(k).type.name)) return true;
  return false;
}

const WORD_RE = /urn:schemas-microsoft-com:office|class="?Mso|mso-|msohtmlclip/i;
function cleanWordHtml(html: string): string {
  if (!WORD_RE.test(html)) return html;
  return html
    .replace(/<!--\[if[\s\S]*?<!\[endif\]-->/gi, (m) => (/<img|imagedata/i.test(m) ? m.replace(/<!--\[if[^>]*>|<!\[endif\]-->/gi, '') : ''))
    .replace(/<o:p>[\s\S]*?<\/o:p>/gi, '')
    .replace(/<\/?(o|v|w|m):[^>]*>/gi, '')
    .replace(/\sstyle="[^"]*"/gi, '')
    .replace(/\sclass="?Mso[^"\s>]*"?/gi, '');
}
